import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Icon } from "@/components/icon";
import { FEATURE } from "@/config/features";
import { buildApiUrl } from "@/config/api";
import { useUserToken } from "@/store/userStore";
import { Button } from "@/ui/button";

/**
 * Visszaigazolásra váró hibajegyek figyelmeztető sávja.
 *
 * A kiosztásról eddig csak e-mail ment, amit a szerelők nem mindig néztek
 * meg. Ez a sáv a belépés után minden oldalon ott marad, amíg a kiosztott
 * jegyeket a felületen vissza nem igazolják — így a diszpécser is látja a
 * jegy listáján, hogy eljutott-e a szerelőhöz.
 */

type PendingComplaint = {
	id: number;
	complaint_number: string;
	address: string | null;
	custom_address: string | null;
	complaint_date: string | null;
	agreed_date: string | null;
	agreed_start_time: string | null;
	complaint_description: string | null;
};

/** Mennyi jegyet mutatunk kinyitás nélkül. */
const LATHATO = 3;

export default function ComplaintAckBanner() {
	// Fejlesztés alatt: élesen a pending-acknowledgement végpont nem létezik.
	// Lásd config/features.ts — alapból KI, hogy ne szivárogjon élesre.
	if (!FEATURE.complaintAcknowledge) return null;

	const userToken = useUserToken();
	const navigate = useNavigate();

	const [jegyek, setJegyek] = useState<PendingComplaint[]>([]);
	const [mind, setMind] = useState(false);
	const [folyamatban, setFolyamatban] = useState<number | null>(null);

	const betoltes = useCallback(async () => {
		if (!userToken.accessToken) return;

		try {
			const valasz = await fetch(buildApiUrl("/customer-complaints/pending-acknowledgement"), {
				method: "GET",
				headers: {
					Accept: "application/json",
					Authorization: `Bearer ${userToken.accessToken}`,
				},
			});
			if (!valasz.ok) return; // néma: a sáv nem akadályozhatja az oldalt
			const eredmeny = await valasz.json();
			setJegyek(eredmeny.data || []);
		} catch {
			// Hálózati hiba esetén nem mutatunk semmit.
		}
	}, [userToken.accessToken]);

	useEffect(() => {
		betoltes();
	}, [betoltes]);

	const visszaigazol = async (id: number) => {
		setFolyamatban(id);
		try {
			const valasz = await fetch(buildApiUrl(`/customer-complaints/${id}/acknowledge`), {
				method: "POST",
				headers: {
					Accept: "application/json",
					Authorization: `Bearer ${userToken.accessToken}`,
				},
			});
			if (!valasz.ok) throw new Error();

			// A sorból helyben vesszük ki: így nem kell újratöltés, és a sáv
			// azonnal eltűnik, ha ez volt az utolsó.
			setJegyek((elozo) => elozo.filter((jegy) => jegy.id !== id));
			toast.success("Hibajegy visszaigazolva");
		} catch {
			toast.error("Nem sikerült visszaigazolni a hibajegyet");
		} finally {
			setFolyamatban(null);
		}
	};

	if (jegyek.length === 0) return null;

	const mutatott = mind ? jegyek : jegyek.slice(0, LATHATO);

	return (
		<div className="mb-4 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700/60 dark:bg-amber-950/40">
			<div className="flex items-start gap-3 px-4 py-3">
				<Icon icon="solar:danger-triangle-bold" size={20} className="mt-0.5 shrink-0 text-amber-600" />
				<div className="min-w-0 flex-1">
					<p className="text-sm font-semibold text-amber-900 dark:text-amber-100">
						{jegyek.length === 1
							? "Egy hibajegy vár a visszaigazolásodra"
							: `${jegyek.length} hibajegy vár a visszaigazolásodra`}
					</p>
					<p className="mt-0.5 text-xs text-amber-800/80 dark:text-amber-200/70">
						Jelezd, hogy megkaptad — enélkül a diszpécser nem tudja, eljutott-e hozzád a jegy.
					</p>

					<ul className="mt-3 space-y-2">
						{mutatott.map((jegy) => (
							<li
								key={jegy.id}
								className="flex flex-wrap items-center gap-x-3 gap-y-2 rounded-md bg-white/70 px-3 py-2 dark:bg-black/20"
							>
								<span className="font-mono text-xs font-semibold">{jegy.complaint_number}</span>
								<span className="min-w-0 flex-1 truncate text-xs">
									{jegy.custom_address || jegy.address || "—"}
									{jegy.agreed_date ? (
										<span className="ml-2 text-amber-700 dark:text-amber-300">
											egyeztetve: {jegy.agreed_date}
											{jegy.agreed_start_time ? ` ${jegy.agreed_start_time}` : ""}
										</span>
									) : null}
								</span>
								<Button
									variant="ghost"
									size="sm"
									className="h-7 px-2 text-xs"
									onClick={() => navigate(`/complaints/view/${jegy.id}`)}
								>
									Megnyitás
								</Button>
								<Button
									size="sm"
									className="h-7 px-3 text-xs"
									disabled={folyamatban === jegy.id}
									onClick={() => visszaigazol(jegy.id)}
								>
									{folyamatban === jegy.id ? "Mentés…" : "Tudomásul vettem"}
								</Button>
							</li>
						))}
					</ul>

					{jegyek.length > LATHATO ? (
						<Button
							variant="ghost"
							size="sm"
							className="mt-2 h-7 px-2 text-xs"
							onClick={() => setMind((elozo) => !elozo)}
						>
							{mind ? "Kevesebb" : `További ${jegyek.length - LATHATO} megjelenítése`}
						</Button>
					) : null}
				</div>
			</div>
		</div>
	);
}
